import { useUser } from '@clerk/react'
import { useEffect, useState } from 'react'
import { Boton } from '@/components/ui/Boton'
import {
  Dialogo,
  DialogoContenido,
  DialogoDescripcion,
  DialogoTitulo,
} from '@/components/ui/Dialogo'

/** Aviso de cada miniatura de Drive: si cargó o no (MediaSlot). */
export const EVENTO_DRIVE = 'rastro:drive'

/** Con dos archivos caídos ya no es un archivo que falta: es la cuenta. */
const MINIMO_FALLAS = 2
/** Con uno solo se espera un momento, por si alguno sí carga. */
const ESPERA_MS = 2_000

/** A dónde escribir cuando no se resuelve eligiendo la cuenta. */
const CONTACTO = 'serena.ruizdiaz@somosrastro.com.ar'

const CLAVE_VISTA = 'rastro:verificacion-vista'

export function avisarDeDrive(id: string, cargo: boolean) {
  window.dispatchEvent(new CustomEvent(EVENTO_DRIVE, { detail: { id, cargo } }))
}

/**
 * P04 · Verificación de cuenta (Fase 6, §8.8). Con dos cuentas de Google
 * abiertas, Drive muestra los archivos con la principal del navegador, que
 * puede no tener permiso: el portal abre pero el contenido no se ve.
 *
 * Las miniaturas de la grilla y de C3 hacen de archivo de prueba. Un archivo
 * suelto que no carga es otra cosa (falta, o no está compartido), así que la
 * pantalla aparece sólo cuando no cargó ninguno y hay al menos dos caídos,
 * una vez por sesión del navegador.
 */
export function VerificacionCuenta() {
  const { user } = useUser()
  const mail = user?.primaryEmailAddress?.emailAddress ?? ''
  const [avisando, setAvisando] = useState(false)
  const [abierta, setAbierta] = useState(false)
  const [copiado, setCopiado] = useState(false)

  useEffect(() => {
    const fallados = new Set<string>()
    let alguno = false
    let reloj: ReturnType<typeof setTimeout> | undefined
    const avisar = () => {
      if (alguno) return
      // Nunca encima de algo que la persona está haciendo: si hay un modal
      // abierto (C3, C5), el aviso de esa pantalla alcanza.
      if (document.querySelector('[role="dialog"]')) return
      let vista = false
      try {
        vista = sessionStorage.getItem(CLAVE_VISTA) === '1'
        sessionStorage.setItem(CLAVE_VISTA, '1')
      } catch {
        // Sin almacenamiento: se muestra igual, una vez por carga.
      }
      if (!vista) setAvisando(true)
    }
    const alAvisar = (e: Event) => {
      const { id, cargo } = (e as CustomEvent<{ id: string; cargo: boolean }>).detail
      if (cargo) {
        alguno = true
        clearTimeout(reloj)
        return
      }
      fallados.add(id)
      if (fallados.size >= MINIMO_FALLAS) {
        clearTimeout(reloj)
        avisar()
        return
      }
      reloj ??= setTimeout(avisar, ESPERA_MS)
    }
    window.addEventListener(EVENTO_DRIVE, alAvisar)
    return () => {
      clearTimeout(reloj)
      window.removeEventListener(EVENTO_DRIVE, alAvisar)
    }
  }, [])

  const elegirCuenta = () => {
    const url = new URL('https://accounts.google.com/AccountChooser')
    if (mail) url.searchParams.set('Email', mail)
    url.searchParams.set('continue', window.location.href)
    window.location.assign(url.toString())
  }

  return (
    <>
      {avisando && (
        // Deja lugar a la barra inferior fija de celular (§5.1), como el Avisador.
        <div className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] sm:pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <p
            role="status"
            data-aviso-verificacion
            className="flex flex-wrap items-center justify-center gap-2 rounded-2xl bg-cambios px-4 py-3 text-sm text-cambios-tinta shadow-tarjeta"
          >
            No podemos mostrarte el contenido de Drive.
            <Boton
              variante="sutil"
              tamano="chico"
              onClick={() => {
                setAbierta(true)
              }}
            >
              Ver por qué
            </Boton>
            <Boton
              variante="sutil"
              tamano="chico"
              aria-label="Cerrar el aviso"
              onClick={() => {
                setAvisando(false)
              }}
            >
              Cerrar
            </Boton>
          </p>
        </div>
      )}

      <Dialogo open={abierta} onOpenChange={setAbierta}>
        <DialogoContenido data-verificacion-cuenta>
          <div className="flex flex-col gap-4 p-6">
            <DialogoTitulo className="text-lg font-semibold">
              No podemos mostrarte el contenido.
            </DialogoTitulo>
            <DialogoDescripcion className="text-sm">
              Tu portal está abierto como <strong>{mail || 'tu cuenta'}</strong>, pero tu
              navegador está mostrando los archivos de Drive con otra cuenta de Google.
            </DialogoDescripcion>
            <Boton onClick={elegirCuenta}>Elegir la cuenta correcta</Boton>
            <div className="flex flex-col gap-1">
              <Boton
                variante="secundario"
                onClick={() => {
                  void navigator.clipboard
                    .writeText(window.location.href)
                    .then(() => {
                      setCopiado(true)
                    })
                    .catch(() => undefined)
                }}
              >
                {copiado ? 'Enlace copiado' : 'Abrir en una ventana de incógnito'}
              </Boton>
              <p className="text-xs text-tinta-suave">
                Copiamos el enlace: abrí una ventana de incógnito (Ctrl+Shift+N), pegalo y
                entrá sólo con {mail || 'tu cuenta'}.
              </p>
            </div>
            <p className="text-center text-sm text-tinta-suave">
              ¿Sigue sin verse?{' '}
              <a className="text-hoy underline" href={`mailto:${CONTACTO}`}>
                Escribinos
              </a>
            </p>
          </div>
        </DialogoContenido>
      </Dialogo>
    </>
  )
}
